import * as React from "react";
import { Theme } from "@mui/material/styles";
import { NotificationsProvider } from "../useNotifications/NotificationsProvider.tsx";
import { DialogsProvider } from "../useDialogs/DialogsProvider.tsx";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  BrandingContext,
  NavigationContext,
  WindowContext,
} from "../shared/context.ts";
import { AppThemeProvider } from "./AppThemeProvider.tsx";
import {
  createBrowserRouter,
  RouteObject,
  RouterProvider,
} from "react-router-dom";
import {
  Authentication,
  Branding,
  Session,
  Navigation,
} from "../../types/types.ts";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";

export const AuthenticationContext = React.createContext<Authentication | null>(
  null,
);

export const SessionContext = React.createContext<Session | null>(null);

export type AppTheme = Theme;

export interface AppProviderProps {
  /**
   * The content of the app provider.
   */
  children?: React.ReactNode;
  /**
   * [Theme or themes](https://mui.com/toolpad/core/react-app-provider/#theming) to be used by the app in light/dark mode. A [CSS variables theme](https://mui.com/material-ui/customization/css-theme-variables/overview/) is recommended.
   * @default createTheme()
   */
  theme: AppTheme;
  /**
   * Branding options for the app.
   * @default null
   */
  branding?: Branding | null;
  /**
   * Navigation definition for the app.
   * @default []
   */
  navigation: Navigation;
  /**
   * Router implementation used inside Toolpad components.
   * @default null
   */
  router: RouteObject[];
  /**
   * Authentication methods.<LocalizationProvider dateAdapter={AdapterDayjs}>
   * @default null
   */
  authentication?: Authentication | null;
  /**
   * The window where the application is rendered.
   * This is needed when rendering the app inside an iframe, for example.
   * @default window
   */
  window?: Window;
}

/**
 *
 * Demos:
 *
 * - [App Provider](https://mui.com/toolpad/core/react-app-provider/)
 * - [Dashboard Layout](https://mui.com/toolpad/core/react-dashboard-layout/)
 *
 * API:
 *
 * - [AppProvider API](https://mui.com/toolpad/core/api/app-provider)
 */
function AppProvider(props: AppProviderProps) {
  const {
    theme,
    branding = null,
    navigation,
    router,
    authentication = null,
    window: appWindow,
  } = props;

  return (
    <WindowContext.Provider value={appWindow}>
      <AuthenticationContext.Provider value={authentication}>
        <AppThemeProvider theme={theme}>
          <NotificationsProvider>
            <DialogsProvider>
              <BrandingContext.Provider value={branding}>
                <NavigationContext.Provider value={navigation}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {/* ToastContainer goes HERE so it gets the theme as well*/}
                    <ToastContainer
                      position="top-right"
                      autoClose={7000}
                      hideProgressBar={false}
                      newestOnTop={false}
                      closeOnClick
                      rtl={false}
                      pauseOnFocusLoss
                      draggable
                      pauseOnHover
                      theme="dark"
                      style={{
                        marginTop: "60px",
                        zIndex: 9999,
                        width: "auto",
                        maxWidth: "auto",
                      }}
                    />
                    <RouterProvider router={createBrowserRouter(router)} />
                  </LocalizationProvider>
                </NavigationContext.Provider>
              </BrandingContext.Provider>
            </DialogsProvider>
          </NotificationsProvider>
        </AppThemeProvider>
      </AuthenticationContext.Provider>
    </WindowContext.Provider>
  );
}

export { AppProvider };
